import React, { useEffect, useState } from "react";
import firebaseApp from "./Firecontact";
import { Container, Row, Col, Badge, Carousel } from "react-bootstrap";
import Header from "./Header";
import Footer from "./Footer";
import { Link } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";

// Professional Skeleton Component - Using gap instead of margin
const ProjectShowcaseSkeleton = ({ isEven }) => {
  return (
    <div className="py-5">
      <Row className="align-items-center g-4 g-md-5">
        {/* Image Skeleton */}
        <Col lg={6} className={isEven ? "order-lg-1" : "order-lg-2"}>
          <div
            className="skeleton-shimmer rounded-3 overflow-hidden"
            style={{ height: "350px" }}
          />
        </Col>

        {/* Content Skeleton */}
        <Col lg={6} className={isEven ? "order-lg-2" : "order-lg-1"}>
          <div className="d-flex flex-column gap-3">
            {/* Badge Skeleton */}
            <div
              className="skeleton-shimmer rounded-pill"
              style={{ width: "120px", height: "32px" }}
            />

            {/* Title Skeleton */}
            <div
              className="skeleton-shimmer rounded"
              style={{ width: "85%", height: "48px" }}
            />

            {/* Description Skeleton */}
            <div className="d-flex flex-column gap-2">
              {[100, 100, 100, 75].map((width, i) => (
                <div
                  key={i}
                  className="skeleton-shimmer rounded"
                  style={{ width: `${width}%`, height: "18px" }}
                />
              ))}
            </div>

            {/* Tech Skeleton */}
            <div className="d-flex flex-wrap gap-2">
              {[80, 100, 70].map((width, i) => (
                <div
                  key={i}
                  className="skeleton-shimmer rounded-pill"
                  style={{ width: `${width}px`, height: "36px" }}
                />
              ))}
            </div>

            {/* Button Skeleton */}
            <div
              className="skeleton-shimmer rounded-pill"
              style={{ width: "200px", height: "52px" }}
            />
          </div>
        </Col>
      </Row>
    </div>
  );
};

const ClientProjects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const db = firebaseApp.firestore();
        const snapshot = await db
          .collection("clientProjects")
          .orderBy("order", "asc")
          .get();

        const allProjects = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        // Filter to show only projects for 9rnfc or both websites
        const filteredProjects = allProjects.filter(
          (p) => p.website === "9rnfc" || p.website === "both" || !p.website
        );
        setProjects(filteredProjects);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching projects:", error);
        setLoading(false);
      }
    };

    fetchProjects();
  }, []);

  return (
    <>
      <div style={{ backgroundColor: '#f7f7fc' }}>
        <Header name="Projects" />

        {/* Hero Section - Same as Product page */}
        <section className="py-3 pt-4" style={{ backgroundColor: '#f7f7fc' }}>
          <div className="container">
            <div className="d-flex justify-content-between align-items-center align-self-center">
              <h3 className="text-dark fw-600 d-none d-md-block pb-0">
                Our <span className="text-orange">Projects</span>
              </h3>
              <ol className="d-flex justify-content-center align-items-center">
                <li>
                  <Link to={"/Home"} className="text-orange">
                    Home
                  </Link>
                </li>
                <span className="mx-2">/</span>
                <li>Projects</li>
              </ol>
            </div>
          </div>
        </section>

        {/* Projects Section */}
        <section className="projects-section">
          <Container>
            {loading ? (
              <div className="d-flex flex-column gap-5">
                {Array.from({ length: 3 }).map((_, i) => (
                  <ProjectShowcaseSkeleton key={i} isEven={i % 2 === 0} />
                ))}
              </div>
            ) : (
              <div className="projects-container">
                {projects.map((project, index) => {
                  const isEven = index % 2 === 0;

                  return (
                    <div
                      key={project.id}
                      className="project-showcase-card position-relative"
                    >
                      {/* Project Number Watermark */}
                      <span className="project-number d-none d-lg-block">
                        {String(index + 1).padStart(2, "0")}
                      </span>

                      <Row className="align-items-center g-4 g-md-5">
                        {/* Image Column */}
                        <Col
                          lg={6}
                          className={`${isEven ? "order-lg-1" : "order-lg-2"} mt-0`}
                        >
                          <div className="project-image-wrapper">
                            {project.images && project.images.length > 1 ? (
                              <Carousel
                                indicators={project.images.length > 1}
                                controls={project.images.length > 1}
                                interval={4000}
                              >
                                {project.images.map((img, idx) => (
                                  <Carousel.Item key={idx}>
                                    <img
                                      className="d-block w-100"
                                      src={img}
                                      alt={`${project.title} - ${idx + 1}`}
                                      style={{
                                        width: "100%",
                                        height: "auto",
                                        backgroundColor: "#f8f9fa",
                                      }}
                                    />
                                  </Carousel.Item>
                                ))}
                              </Carousel>
                            ) : (
                              <img
                                src={
                                  project.images && project.images[0]
                                    ? project.images[0]
                                    : ""
                                }
                                alt={project.title}
                                className="w-100"
                                style={{
                                  width: "100%",
                                  height: "auto",
                                  backgroundColor: "#f8f9fa",
                                }}
                              />
                            )}
                          </div>
                        </Col>

                        {/* Content Column */}
                        <Col
                          lg={6}
                          className={isEven ? "order-lg-2" : "order-lg-1"}
                        >
                          <div
                            className="project-content-wrapper position-relative"
                            style={{ zIndex: 1 }}
                          >
                            {/* Category Badge */}
                            <Badge className="project-category-badge px-4 py-2 fw-500 text-uppercase">
                              {project.category}
                            </Badge>

                            {/* Title */}
                            <h2 className="project-title fw-bold text-dark">
                              {project.title}
                            </h2>

                            {/* Description */}
                            <p className="project-description text-secondary">
                              {project.description}
                            </p>

                            {/* Technologies */}
                            {project.technologies && (
                              <div className="tech-stack-container">
                                <h6
                                  className="fw-bold text-dark d-flex align-items-center gap-2"
                                  style={{
                                    fontSize: "0.85rem",
                                    letterSpacing: "0.5px",
                                    margin: 0,
                                  }}
                                >
                                  <i
                                    className="lni lni-code-alt text-primary"
                                    style={{ fontSize: "1.1rem" }}
                                  ></i>
                                  TECH STACK
                                </h6>
                                {Array.isArray(project.technologies) ? (
                                  <div className="d-flex flex-wrap gap-2">
                                    {project.technologies.map((tech, idx) => (
                                      <div
                                        key={idx}
                                        className="tech-badge d-flex align-items-center px-3 py-2"
                                      >
                                        {tech.logo && (
                                          <img
                                            src={tech.logo}
                                            alt={tech.name}
                                          />
                                        )}
                                        <span className="text-dark">
                                          {tech.name}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <p
                                    className="text-secondary"
                                    style={{ margin: 0 }}
                                  >
                                    {project.technologies}
                                  </p>
                                )}
                              </div>
                            )}

                            {/* Testimonial */}
                            {project.testimonial && (
                              <div className="testimonial-box position-relative d-flex flex-column gap-2">
                                <i className="lni lni-quotation position-absolute text-warning quote-icon"></i>
                                <p className="fst-italic text-dark">
                                  &quot;{project.testimonial}&quot;
                                </p>
                                <small className="text-muted fw-600">
                                  — Client Feedback
                                </small>
                              </div>
                            )}

                            {/* CTA Button */}
                            {project.link && (
                              <a
                                href={project.link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="project-cta-btn btn-main px-5 py-3 fw-600 border-0 align-items-center d-flex justify-content-center text-white text-decoration-none"
                                style={{
                                  borderRadius: "50px",
                                  fontSize: "0.95rem",
                                  width: "fit-content",
                                }}
                              >
                                {project.buttonText || "View Live Project"}
                                <i className="lni lni-arrow-right ms-2"></i>
                              </a>
                            )}
                          </div>
                        </Col>
                      </Row>
                    </div>
                  );
                })}
              </div>
            )}
          </Container>
        </section>
        <Footer />
      </div>
    </>
  );
};

export default ClientProjects;
